import {
  useCallback,
  useMemo,
  useState,
  type ComponentType,
  type FC,
} from "react";
import type { Dayjs } from "dayjs";

import DatePickerJs from "../../../shared/DatePicker/DatePicker";
import ExportToXlsxButtonJs from "../../../CommonComponents/ExportToXlsxButton/ExportToXlsxButton";
import { Select } from "../../../shared";
import { BarChart, CHART_PALETTE, ChartCard } from "../../../shared/Charts";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatDateNoTimezone,
  getAdminNow,
} from "../../../../utils/dateService";
import { useAffiliateDailyUsersFtds } from "../../../../queries/affiliates/useAffiliateTimeSeries";
import type { AffiliateAnalyticsDataSource } from "../../../../queries/affiliates/analyticsDataSource";
import type { AffiliateKpisScope } from "../../affiliatesData";
import { useChartLocalFilters } from "../useChartLocalFilters";

import styles from "./DailyUsersAndFtds.module.css";

const CustomDatePicker = DatePickerJs as ComponentType<{
  dates: DateRange;
  setDates: (dates: DateRange) => void;
  getData: (queryString: string | null) => void;
  showQuickSelect?: boolean;
  showPresets?: boolean;
  startRange?: string;
}>;

const ExportToXlsxButton = ExportToXlsxButtonJs as ComponentType<{
  data: Record<string, unknown>[];
  filename: string;
}>;

type DateRange = [Dayjs, Dayjs];

const SERIES = [
  { key: "users", color: CHART_PALETTE.seriesGreen },
  { key: "ftds", color: CHART_PALETTE.seriesPurple },
] as const;

const DailyUsersAndFtds: FC<{
  scope?: AffiliateKpisScope;
  affiliateId?: string;
  dataSource?: AffiliateAnalyticsDataSource;
  // Cross-cut filters lifted from the KPIs tab's global filter dropdown.
  // Passed through to the hook so this chart re-fetches when the operator
  // narrows the scope. The card's own inline All Affiliates / All Codes
  // selects override these while set (see useChartLocalFilters).
  affiliate?: string;
  promoCode?: string;
  // Options for the card's own selects. Codes cover the cross-cut
  // affiliate; a chart-local affiliate pick fetches its own code list.
  affiliateOptions?: ReadonlyArray<{ value: string; label: string }>;
  isLoadingAffiliates?: boolean;
  codeOptions?: ReadonlyArray<{ value: string; label: string }>;
  isLoadingCodes?: boolean;
  // False → render empty without fetching (previews of an affiliate that
  // doesn't exist yet).
  queryEnabled?: boolean;
}> = ({
  scope = "module",
  affiliateId,
  dataSource = "operator",
  affiliate,
  promoCode,
  affiliateOptions,
  isLoadingAffiliates = false,
  codeOptions,
  isLoadingCodes = false,
  queryEnabled = true,
}) => {
  const [dates, setDates] = useState<DateRange>(() => [
    getAdminNow().subtract(30, "day").startOf("day"),
    getAdminNow().endOf("day"),
  ]);
  const {
    selectedAffiliate,
    setSelectedAffiliate,
    selectedCode,
    setSelectedCode,
    filters,
    codeSelect,
  } = useChartLocalFilters({ affiliate, promoCode, codeOptions, isLoadingCodes });

  const seriesLabels: Record<(typeof SERIES)[number]["key"], string> = {
    users: AppStrings.USERS,
    ftds: AppStrings.FIRST_TIME_DEPOSITS,
  };

  const query = useAffiliateDailyUsersFtds(
    {
      start_date: dates[0].format("YYYY-MM-DD"),
      end_date: dates[1].format("YYYY-MM-DD"),
    },
    affiliateId,
    filters,
    dataSource,
    { enabled: queryEnabled },
  );
  const points = useMemo(() => query.data ?? [], [query.data]);

  const getData = useCallback((_queryString: string | null) => {
    // Date changes flow through `dates` -> the query key, which refetches.
  }, []);

  const chartData = useMemo(
    () => ({
      labels: points.map((p) => formatDateNoTimezone(p.date, "MMM D")),
      datasets: SERIES.map((s) => ({
        label: seriesLabels[s.key],
        data: points.map((p) => p[s.key]),
        backgroundColor: s.color,
        borderRadius: 3,
        barPercentage: 1.0,
        categoryPercentage: 0.7,
      })),
    }),
    // seriesLabels is rebuilt every render — string lookups, not data —
    // so deps stay on the underlying points.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [points],
  );

  const chartOptions = useMemo(
    () => ({
      datasets: {
        bar: {
          barPercentage: 1.0,
          categoryPercentage: 0.7,
        },
      },
      interaction: { mode: "index" as const, intersect: false },
      scales: {
        y: { beginAtZero: true },
      },
      plugins: {
        tooltip: {
          mode: "index" as const,
          intersect: false,
          displayColors: true,
        },
      },
    }),
    [],
  );

  const exportRows = useMemo(
    () =>
      points.map((p) => ({
        date: p.date,
        users: p.users,
        first_time_deposits: p.ftds,
      })),
    [points],
  );

  const legend = (
    <ul className={styles.legend}>
      {SERIES.map((s) => (
        <li key={s.key} className={styles.legendItem}>
          <span
            className={styles.legendDot}
            style={{ backgroundColor: s.color }}
            aria-hidden="true"
          />
          {seriesLabels[s.key]}
        </li>
      ))}
    </ul>
  );

  return (
    <ChartCard
      title={AppStrings.DAILY_USERS_AND_FTDS}
      headerLayout="column"
      headerExtra={legend}
      bodyClassName={styles.chartContainer}
      controls={
        <>
          {scope === "module" && (
            <Select<string>
              className={styles.filterSelect}
              placeholder={AppStrings.ALL_AFFILIATES}
              value={selectedAffiliate}
              onChange={(value) => setSelectedAffiliate(value)}
              allowClear
              showSearch
              optionFilterProp="label"
              options={affiliateOptions ? [...affiliateOptions] : []}
              loading={isLoadingAffiliates}
            />
          )}
          <Select<string>
            className={styles.filterSelect}
            placeholder={AppStrings.ALL_CODES}
            value={selectedCode}
            onChange={(value) => setSelectedCode(value)}
            allowClear
            showSearch
            optionFilterProp="label"
            options={codeSelect.options}
            loading={codeSelect.loading}
            disabled={codeSelect.disabled}
          />
          <CustomDatePicker
            getData={getData}
            dates={dates}
            setDates={setDates}
            showPresets
          />
          <ExportToXlsxButton
            data={exportRows}
            filename="affiliates-daily-users-and-ftds.xlsx"
          />
        </>
      }
    >
      <BarChart data={chartData} optionsOverride={chartOptions} />
    </ChartCard>
  );
};

export default DailyUsersAndFtds;
